/**
 * StudioRoom Component - Main recording interface
 *
 * Features:
 * - Multi-participant video grid
 * - Recording controls
 * - Screen sharing
 * - Chat sidebar
 * - Guest management
 * - Real-time status
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useStudioStore } from '../App';
import { createStudioSession, StudioSession, Participant, ChatMessage } from '../lib/session';
import { RecordingTrack } from '../lib/recorder';
import { ParticipantTile } from './ParticipantTile';
import { Controls } from './Controls';
import { ChatPanel } from './ChatPanel';
import { InviteModal } from './InviteModal';
import { CompactMetricsIndicator } from './ConnectionMetrics';

export function StudioRoom() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Store
  const { token, userId, userName, sessionConfig, sessionState, setSessionState, clearSession } = useStudioStore();

  // Session
  const [session, setSession] = useState<StudioSession | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);

  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [_tracks, setTracks] = useState<RecordingTrack[]>([]);

  // UI State
  const [showChat, setShowChat] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recording timer
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Initialize session
  useEffect(() => {
    async function initSession() {
      // Extract guest params from URL
      const guestMeetingId = searchParams.get('meeting');
      const guestVideoSDKToken = searchParams.get('token');
      const isGuest = !!(guestMeetingId && guestVideoSDKToken);

      if (!sessionConfig && !isGuest) {
        // No session config and not joining as guest
        navigate('/');
        return;
      }

      try {
        // If guest, create minimal config
        const config = sessionConfig || {
          title: 'Guest Session',
          hostId: userId || 'guest',
          hostName: userName || 'Guest',
          quality: '1080p' as const,
          enableChat: true,
          enableGreenRoom: false,
          enableRecording: true,
          maxParticipants: 10
        };

        const newSession = createStudioSession(config, token || '', {
          onParticipantJoined: (participant) => {
            console.log('[StudioRoom] Participant joined:', participant.name);
            setParticipants(prev => [...prev, participant]);
          },
          onParticipantLeft: (participantId) => {
            console.log('[StudioRoom] Participant left:', participantId);
            setParticipants(prev => prev.filter(p => p.id !== participantId));
          },
          onParticipantUpdated: (participant) => {
            setParticipants(prev =>
              prev.map(p => p.id === participant.id ? participant : p)
            );
          },
          onChatMessage: (message) => {
            setChatMessages(prev => [...prev, message]);
            if (!showChat) {
              setUnreadMessages(prev => prev + 1);
            }
          },
          onStateChange: (state) => {
            setSessionState(state);
            if (state === 'recording') {
              setIsRecording(true);
            } else if (state === 'live' || state === 'ended') {
              setIsRecording(false);
            }
          },
          onRecordingStart: () => {
            console.log('[StudioRoom] Recording started');
            setIsRecording(true);
            startTimer();
          },
          onRecordingStop: (recordedTracks) => {
            console.log('[StudioRoom] Recording stopped, tracks:', recordedTracks.length);
            setIsRecording(false);
            stopTimer();
            setTracks(recordedTracks);
          },
          onError: (err) => {
            console.error('[StudioRoom] Error:', err);
            setError(err.message);
          }
        });

        // Initialize differently based on host vs guest
        if (isGuest && guestMeetingId && guestVideoSDKToken) {
          // Guest joins existing meeting with VideoSDK token from URL
          console.log('[StudioRoom] Initializing as guest with meeting:', guestMeetingId);
          await newSession.initializeAsGuest(guestMeetingId, guestVideoSDKToken);
        } else {
          // Host creates new meeting
          await newSession.initialize();
        }

        setSession(newSession);
        setLocalStream(newSession.getLocalStream());
        setParticipants(newSession.getParticipants());

        // Auto go-live if not using green room
        if (!config.enableGreenRoom) {
          await newSession.goLive();
        }
      } catch (err) {
        console.error('[StudioRoom] Init failed:', err);
        setError('Failed to initialize session');
      }
    }

    initSession();

    return () => {
      stopTimer();
      if (session) {
        session.endSession();
      }
    };
  }, [sessionConfig, token, userId, userName, sessionId, searchParams, navigate, setSessionState, showChat]);

  // Timer functions
  const startTimer = () => {
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return hrs > 0
      ? `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Control handlers
  const handleToggleMute = useCallback(() => {
    if (session) {
      const muted = session.toggleMute();
      console.log('[StudioRoom] Muted:', muted);
    }
  }, [session]);

  const handleToggleVideo = useCallback(() => {
    if (session) {
      const videoOff = session.toggleVideo();
      console.log('[StudioRoom] Video off:', videoOff);
    }
  }, [session]);

  const handleStartRecording = useCallback(async () => {
    if (session && sessionState === 'live') {
      try {
        await session.startRecording();
      } catch (err) {
        console.error('[StudioRoom] Failed to start recording:', err);
        setError('Failed to start recording');
      }
    }
  }, [session, sessionState]);

  const handleStopRecording = useCallback(async () => {
    if (session && sessionState === 'recording') {
      try {
        await session.stopRecording();
      } catch (err) {
        console.error('[StudioRoom] Failed to stop recording:', err);
        setError('Failed to stop recording');
      }
    }
  }, [session, sessionState]);

  const handleGoLive = useCallback(async () => {
    if (session && sessionState === 'green-room') {
      try {
        await session.goLive();
      } catch (err) {
        console.error('[StudioRoom] Failed to go live:', err);
        setError('Failed to go live');
      }
    }
  }, [session, sessionState]);

  const handleScreenShare = useCallback(async () => {
    if (!session) return;

    if (isScreenSharing) {
      session.stopScreenShare();
      setScreenStream(null);
      setIsScreenSharing(false);
    } else {
      try {
        await session.startScreenShare();
        setScreenStream(session.getScreenStream());
        setIsScreenSharing(true);
      } catch (err) {
        console.error('[StudioRoom] Screen share failed:', err);
        setError('Failed to start screen sharing');
      }
    }
  }, [session, isScreenSharing]);

  const handleEndSession = useCallback(async () => {
    if (session) {
      const confirmed = window.confirm(
        isRecording
          ? 'Recording in progress. Stop recording and end session?'
          : 'Are you sure you want to end the session?'
      );

      if (confirmed) {
        await session.endSession();
        clearSession(); // Clear sessionStorage so we don't loop back
        navigate('/recordings');
      }
    }
  }, [session, isRecording, navigate, clearSession]);

  const handleSendMessage = useCallback((message: string) => {
    if (session) {
      session.sendMessage(message);
    }
  }, [session]);

  const handleToggleChat = useCallback(() => {
    setShowChat(prev => !prev);
    if (!showChat) {
      setUnreadMessages(0);
    }
  }, [showChat]);

  // Get local participant
  const localParticipant = participants.find(p => p.isLocal);
  const remoteParticipants = participants.filter(p => !p.isLocal);

  // Grid layout based on participant count
  const getGridLayout = () => {
    const total = participants.length + (screenStream ? 1 : 0);
    if (total <= 1) return 'single';
    if (total <= 2) return 'two';
    if (total <= 4) return 'four';
    if (total <= 6) return 'six';
    return 'many';
  };

  return (
    <div className="studio-room">
      {/* Header */}
      <div className="studio-header">
        <div className="session-info">
          <h1>{sessionConfig?.title || 'Recording Session'}</h1>
          <span className={`session-state ${sessionState}`}>
            {sessionState === 'green-room' && '🟡 Green Room'}
            {sessionState === 'live' && '🟢 Live'}
            {sessionState === 'recording' && '🔴 Recording'}
            {sessionState === 'ended' && '⚫ Ended'}
          </span>
        </div>

        {/* Recording Timer */}
        {isRecording && (
          <div className="recording-indicator">
            <span className="rec-dot"></span>
            <span className="rec-time">{formatTime(recordingTime)}</span>
          </div>
        )}

        {/* Connection Quality Metrics */}
        <CompactMetricsIndicator
          audioStream={localStream}
          isHost={!searchParams.get('meeting')}
        />

        {/* Header Actions */}
        <div className="header-actions">
          <button
            className="header-btn"
            onClick={() => setShowInvite(true)}
            title="Invite Guests"
          >
            👥 Invite
          </button>
          <button
            className={`header-btn ${showChat ? 'active' : ''}`}
            onClick={handleToggleChat}
            title="Toggle Chat"
          >
            💬 Chat
            {unreadMessages > 0 && (
              <span className="badge">{unreadMessages}</span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="studio-content">
        {/* Video Grid */}
        <div className={`video-grid layout-${getGridLayout()}`}>
          {/* Screen Share (if active) */}
          {screenStream && (
            <div className="screen-share-tile">
              <video
                autoPlay
                playsInline
                muted
                ref={(el) => {
                  if (el) el.srcObject = screenStream;
                }}
              />
              <div className="tile-label">Screen Share</div>
            </div>
          )}

          {/* Local Participant */}
          {localParticipant && (
            <ParticipantTile
              participant={localParticipant}
              stream={localStream}
              isLocal={true}
            />
          )}

          {/* Remote Participants */}
          {remoteParticipants.map(participant => (
            <ParticipantTile
              key={participant.id}
              participant={participant}
              isLocal={false}
            />
          ))}

          {/* Empty State */}
          {participants.length === 1 && (
            <div className="empty-tile">
              <span>👥</span>
              <p>Waiting for guests to join...</p>
              <button
                className="invite-btn"
                onClick={() => setShowInvite(true)}
              >
                Share Invite Link
              </button>
            </div>
          )}
        </div>

        {/* Chat Sidebar */}
        {showChat && (
          <ChatPanel
            messages={chatMessages}
            onSendMessage={handleSendMessage}
            onClose={() => setShowChat(false)}
          />
        )}
      </div>

      {/* Controls Bar */}
      <Controls
        isMuted={localParticipant?.isMuted || false}
        isVideoOff={localParticipant?.isVideoOff || false}
        isRecording={isRecording}
        isScreenSharing={isScreenSharing}
        sessionState={sessionState}
        onToggleMute={handleToggleMute}
        onToggleVideo={handleToggleVideo}
        onStartRecording={handleStartRecording}
        onStopRecording={handleStopRecording}
        onGoLive={handleGoLive}
        onScreenShare={handleScreenShare}
        onEndSession={handleEndSession}
      />

      {/* Error Toast */}
      {error && (
        <div className="error-toast">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && session && (
        <InviteModal
          session={session}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}
